import React, { useRef } from "react";
import { styled } from "../../../theme/styled";
import { useOnMount } from "../../../util/useOnMount/useOnMount";
import { useUniqueId } from "../../../util/useUniqueId";
import { AlertFilledIcon } from "../../icons/new-icons/icons";
import { font } from "../../typography/baseStyling";
import { FormItemLabel } from "../FormItemLabel/FormItemLabel";
import { Message } from "../shared/Message";
import { textFieldHeight } from "../shared/textFieldUtils";

export interface Props extends
  Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    | "autoComplete"
    | "autoFocus"
    | "disabled"
    | "inputMode"
    | "name"
    | "pattern"
    | "placeholder"
    | "readOnly"
    | "required"
    | "value"
  > /**
   * NOTE: Intentionally not picking `onBlur` or `onChange` here since their argument types differ from `Props['onBlur']` and `Props['onChange']`
   */ {
  /* tells screenreader if the input element is in invalid/error state or not */
  ariaInvalid?: boolean;
  /* used for testing purposes */
  dataTestId?: string;
  /* description message below input element, used to be tooltip */
  descriptionMessage?: string | null;
  /* error message below input element, often used when in invalid/error state */
  errorMessage?: string | null;
  /* longer text to describe label */
  helpText?: string;
  // help text specifically for screen readers. If provided, this will replace helpText when using screen reader.
  screenReaderHelpText?: string;
  /* label text for input field */
  label?: string;
  /* optional onBlur event occurs when the field loses focus */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /* optional function to use when input element value changes */
  onTextChange?: (str: string) => void;
  /* On value change, preferably use onTextChange prop instead of this */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /* When input element is focused */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /* text placed to the far right of the label, commonly used to describe whether the input field is required or not */
  labelEndText?: string;
  /* size of the input element */
  size?: Size;
  /* type of input for text field */
  type?: InputType;
  /* warning message below input element, often used to describe information */
  warningMessage?: string | null;
  /* Reference to inner input */
  inputRef?: React.RefCallback<HTMLInputElement>;
  maxLength?: number;
  minLength?: number;
}

type InputType = "text" | "email" | "number" | "password" | "tel";

type Size = "small" | "medium" | "large";

type Severity = "info" | "warning" | "error";

export const TextField = ({
  ariaInvalid = false,
  autoFocus = false,
  dataTestId,
  descriptionMessage,
  disabled = false,
  errorMessage,
  helpText,
  screenReaderHelpText,
  label,
  onBlur,
  onTextChange,
  onChange,
  onFocus,
  labelEndText,
  readOnly = false,
  required,
  autoComplete,
  name,
  size = "medium",
  type = "text",
  value,
  warningMessage,
  inputRef: innerRefCallback,
  placeholder,
  inputMode,
  pattern,
  maxLength,
  minLength,
}: Props): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useOnMount(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  });

  const severity = errorMessage ? "error" : warningMessage ? "warning" : "info";
  const message = warningMessage || errorMessage || descriptionMessage;

  const id = useUniqueId();
  const messageId = `${id}-message`;

  return (
    <div data-test-id={dataTestId} data-component-type={dataTestId}>
      <FormItemLabel
        {...{
          helpText,
          screenReaderHelpText,
          htmlFor: id,
          titleText: label,
          endText: labelEndText,
          size: size === "small" ? "small" : "medium",
        }}
      />
      <InputWrapper>
        <Input
          id={id}
          aria-invalid={ariaInvalid || Boolean(errorMessage)}
          aria-describedby={message ? messageId : undefined}
          required={required}
          autoComplete={autoComplete}
          type={type}
          name={name}
          value={value}
          onBlur={onBlur}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onTextChange?.(event.target.value);
            onChange?.(event);
          }}
          onFocus={onFocus}
          readOnly={readOnly}
          disabled={disabled}
          severity={severity}
          errorIcon={Boolean(errorMessage)}
          inputSize={size}
          ref={(el) => {
            inputRef.current = el;
            innerRefCallback?.(el);
          }}
          placeholder={placeholder}
          // Allow any numerical incrementation. Otherwise
          // WebKit browsers' validation will fail on decimals.
          step={type === "number" ? "any" : undefined}
          inputMode={inputMode}
          pattern={pattern}
          maxLength={maxLength}
          minLength={minLength}
        />
        {errorMessage && (
          <Icon
            align="right"
            data-test-id={dataTestId
              ? `${dataTestId}-error-message-icon`
              : undefined}
          >
            <AlertFilledIcon size={24} colorToken="$error-text" />
          </Icon>
        )}
      </InputWrapper>
      {message && (
        <Message
          severity={severity}
          size={size}
          message={message}
          id={messageId}
        />
      )}
    </div>
  );
};

const InputWrapper = styled.div({
  position: "relative",
});

export interface InputProps {
  inputSize: Size;
  disabled?: Props["disabled"];
  readOnly?: Props["readOnly"];
  severity: Severity;
  errorIcon?: boolean;
}

export const Input = styled("input")<InputProps>((p) => {
  const sizeStyles = {
    small: {
      ...font.BodyS,
      textAlign: "left",
      height: textFieldHeight.small,
    },
    medium: {
      ...font.BodyM,
      textAlign: "left",
      height: textFieldHeight.medium,
    },
    large: {
      ...font.HeadingL,
      textAlign: "center",
      height: textFieldHeight.large,
    },
  } as const;
  return {
    margin: "$spacing-0",
    display: "block",
    padding: "$spacing-16",
    background: p.disabled || p.readOnly
      ? "$surface-disabled"
      : "$background-secondary",
    color: p.disabled || p.readOnly ? "$on-surface-disabled" : "$text-primary",
    cursor: p.disabled || p.readOnly ? "not-allowed" : "inherit",
    WebkitTapHighlightColor: "transparent",
    borderRadius: "$radius-small",
    border: "1px solid $border-input",
    borderColor: p.severity === "error" ? "$error-text" : "$border-input",
    boxSizing: "border-box",
    width: "100%",
    outline: 0,
    WebkitAppearance: "none",
    paddingRight: p.errorIcon ? "$spacing-48" : undefined,
    ...sizeStyles[p.inputSize],
    "&::placeholder": {
      color: "$text-secondary",
    },
    "&:hover": {
      boxShadow: p.severity === "error"
        ? "inset 0 0 0 1px $error-text"
        : "inset 0 0 0 1px $border-input",
    },

    "&:focus": {
      boxShadow: p.severity === "error"
        ? "inset 0 0 0 1px $error-text"
        : "inset 0 0 0 1px $active-highlight",
      borderColor: p.severity === "error" ? "$error-text" : "$active-highlight",
    },
  };
});

const Icon = styled.div<{ align: "right" | "left" }>((p) => ({
  display: "flex",
  height: "100%",
  alignItems: "center",
  position: "absolute",
  right: p.align === "right" ? "15px" : "",
  left: p.align === "left" ? "15px" : "",
  top: "0",
}));
